// Главный оркестратор упражнения для студента: поддерживает типы keyword_transform и lexics.
// Управляет пагинацией по предложениям, прогрессом, прерыванием и отправкой ответов.

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Container, Card, Button, Badge, ProgressBar, Modal, Spinner, Alert,
} from 'react-bootstrap';
import AppNavbar from '../../common/AppNavbar';
import KeywordTransformItem from './KeywordTransformItem';
import LexicsTextItem from './LexicsTextItem';
import { completeVariant, interruptVariant } from '../../../api/studentApi';

const LEVEL_LABEL   = { 1: 'Beginner', 2: 'Elementary', 3: 'Pre-Intermediate', 4: 'Intermediate' };
const LEVEL_VARIANT = { 1: 'success', 2: 'info', 3: 'primary', 4: 'warning' };

// Парсинг шаблона для lexics
function parseSentence(sentence) {
  const tokens = [];
  let remaining = sentence ?? '';
  let autoPos = 0;
  let safety = 0;
  while (remaining.length > 0 && safety++ < 500) {
    const match = remaining.match(/^(.*?)(?:%(\d+)|_{5,})(.*)/s);
    if (!match) {
      tokens.push({ type: 'text', value: remaining });
      break;
    }
    if (match[1]) tokens.push({ type: 'text', value: match[1] });
    const pos = match[2] !== undefined ? match[2] : String(++autoPos);
    tokens.push({ type: 'blank', pos });
    remaining = match[3];
  }
  return tokens;
}

/**
 * StudentExercise — главный оркестратор для keyword_transform и lexics.
 *
 * Props:
 *   variant:    object         — данные варианта (sentences, form_type, grammar_rule, ...)
 *   historyId:  number         — id истории (из startVariant)
 *   exerciseId: string|number
 *   variantId:  string|number
 *   navigate:   function
 *   onInterrupt: function (optional)
 */
export default function StudentExercise({
  variant, historyId, exerciseId, variantId, navigate, onInterrupt, previewMode = false,
  completeVariantFn = null,   // optional: (variantId, payload) => Promise<{data: result}>
  resultNavigateFn = null,    // optional: (result) => void — called instead of default navigate
}) {
  const formType = variant?.form_type ?? 'keyword_transform';

  // ── Предложения / items ─────────────────────────────────────────────────────
  const sentenceEntries = useMemo(
    () => Object.entries(variant?.sentences ?? {}).sort(([a], [b]) => Number(a) - Number(b)),
    [variant?.sentences],
  );

  // Для keyword_transform: каждое предложение — отдельный item (пагинация)
  // Для lexics: одно большое упражнение (всегда один item)
  const isKeyword = formType === 'keyword_transform';
  const isLexics  = formType === 'lexics';

  // primarySentId для lexics
  const primarySentId = sentenceEntries[0]?.[0] ?? '1';
  const primarySent   = sentenceEntries[0]?.[1] ?? { sentence: '', words: {} };
  const lexicsTokens  = useMemo(() => parseSentence(primarySent.sentence), [primarySent.sentence]);
  const lexicsWordsBank = primarySent.words ?? {};

  // Количество items (для прогресса в keyword_transform)
  const totalItems = isKeyword ? sentenceEntries.length : 1;

  // ── State ───────────────────────────────────────────────────────────────────
  const [currentIdx, setCurrentIdx]             = useState(0);
  // answers: { sentId: { blankPos: value } }  (для lexics: { primarySentId: { blankPos: word } })
  const [answers, setAnswers]                   = useState({});
  // progressStep — количество пройденных предложений (только при нажатии «Вперёд»/завершении)
  const [progressStep, setProgressStep]         = useState(0);
  const [submitting, setSubmitting]             = useState(false);
  const [submitError, setSubmitError]           = useState('');
  const [showInterruptModal, setShowInterruptModal] = useState(false);
  const [interrupting, setInterrupting]         = useState(false);
  const [showRuleModal, setShowRuleModal]       = useState(false);

  // ref на первый input текущего элемента — для автофокуса
  const firstInputRef = useRef(null);

  // ── Прогресс ────────────────────────────────────────────────────────────────
  // answeredCount используется только для счётчика «Осталось заполнить» текущего предложения
  const { totalBlanks, answeredCount } = useMemo(() => {
    if (isKeyword) {
      let total = 0;
      let filled = 0;
      for (const [sentId, sentObj] of sentenceEntries) {
        const blanks = Object.keys(sentObj.words ?? {});
        total += blanks.length;
        filled += blanks.filter((bp) => (answers[sentId]?.[bp] ?? '').trim() !== '').length;
      }
      return { totalBlanks: total, answeredCount: filled };
    }
    // lexics
    const blanks = Object.keys(lexicsWordsBank);
    const filled = blanks.filter((bp) => (answers[primarySentId]?.[bp] ?? '') !== '').length;
    return { totalBlanks: blanks.length, answeredCount: filled };
  }, [isKeyword, sentenceEntries, answers, lexicsWordsBank, primarySentId]);

  // Прогресс-бар двигается только при нажатии «Вперёд» (через progressStep)
  const progress = isKeyword && totalItems > 1
    ? Math.round((progressStep / totalItems) * 100)
    : totalBlanks > 0 ? Math.round((answeredCount / totalBlanks) * 100) : 0;

  // ── Проверка текущего item ───────────────────────────────────────────────────
  const isCurrentItemFilled = useMemo(() => {
    if (isKeyword) {
      const [sentId, sentObj] = sentenceEntries[currentIdx] ?? [];
      if (!sentId) return false;
      const blanks = Object.keys(sentObj?.words ?? {});
      return blanks.length === 0 || blanks.every((bp) => (answers[sentId]?.[bp] ?? '').trim() !== '');
    }
    // lexics — все пропуски заполнены
    const blanks = Object.keys(lexicsWordsBank);
    return blanks.length === 0 || blanks.every((bp) => (answers[primarySentId]?.[bp] ?? '') !== '');
  }, [isKeyword, sentenceEntries, currentIdx, answers, lexicsWordsBank, primarySentId]);

  const isLastItem = isKeyword ? currentIdx >= sentenceEntries.length - 1 : true;

  // ── Обработчики ответов ─────────────────────────────────────────────────────
  const handleSetAnswer = useCallback((sentId, blankPos, value) => {
    setAnswers((prev) => ({
      ...prev,
      [sentId]: { ...(prev[sentId] ?? {}), [blankPos]: value },
    }));
  }, []);

  // Для lexics
  const handleWordClick = useCallback((blankPos, word) => {
    setAnswers((prev) => ({
      ...prev,
      [primarySentId]: { ...(prev[primarySentId] ?? {}), [blankPos]: word },
    }));
  }, [primarySentId]);

  const handleClearBlank = useCallback((blankPos) => {
    setAnswers((prev) => {
      const inner = { ...(prev[primarySentId] ?? {}) };
      delete inner[blankPos];
      return { ...prev, [primarySentId]: inner };
    });
  }, [primarySentId]);

  // ── Автофокус на первый input при переходе к новому предложению ──────────────
  useEffect(() => {
    if (!isKeyword) return;
    const timer = setTimeout(() => {
      if (firstInputRef.current) firstInputRef.current.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, [currentIdx, isKeyword]);

  // ── Enter → Вперёд / Проверить ──────────────────────────────────────────────
  useEffect(() => {
    if (!isKeyword) return;
    const handleKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      if (isLastItem) {
        if (isCurrentItemFilled && !submitting) {
          setProgressStep(totalItems);
          handleComplete();
        }
      } else {
        setCurrentIdx((i) => i + 1);
        setProgressStep((s) => Math.min(totalItems, s + 1));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isKeyword, isLastItem, isCurrentItemFilled, submitting, totalItems]);

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleComplete = async () => {
    if (previewMode) {
      if (onInterrupt) onInterrupt();
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      const payload = {
        history_id: historyId,
        answer: JSON.stringify(answers),
      };
      const { data: result } = await (completeVariantFn ?? completeVariant)(variantId, payload);
      if (resultNavigateFn) {
        resultNavigateFn(result);
      } else {
        navigate('/student/exercises/result', { state: { result, exerciseId, variantId } });
      }
    } catch {
      setSubmitError('Не удалось отправить ответы. Попробуйте ещё раз.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Interrupt ───────────────────────────────────────────────────────────────
  const handleInterrupt = async () => {
    if (previewMode) {
      setShowInterruptModal(false);
      if (onInterrupt) onInterrupt();
      return;
    }
    setInterrupting(true);
    try {
      await interruptVariant(variantId, { history_id: historyId });
    } catch {
      // ignore
    } finally {
      setInterrupting(false);
      setShowInterruptModal(false);
      if (onInterrupt) {
        onInterrupt();
      } else {
        navigate('/student/exercises');
      }
    }
  };

  const complexityLevel = variant?.complexity_level;
  const complexityId    = variant?.complexity_level_id;
  const explanation     = variant?.grammar_rule ?? null;

  // ── Текущий item для keyword_transform ──────────────────────────────────────
  const currentEntry = sentenceEntries[currentIdx];

  return (
    <>
      <AppNavbar />

      <div style={{ background: 'var(--ewl-bg-body)', minHeight: 'calc(100vh - 56px)' }}>
        <Container className="py-4" style={{ maxWidth: isLexics ? 1100 : 760 }}>

          {/* ── Заголовок ── */}
          <div className="mb-3">
            <div className="mb-2">
              <Button
                variant="link"
                size="sm"
                onClick={() => previewMode ? onInterrupt?.() : setShowInterruptModal(true)}
                style={{ padding: 0, color: '#6c757d', textDecoration: 'none', fontWeight: 500, fontSize: '0.875rem' }}
              >
                {previewMode ? '← Закрыть просмотр' : '← Назад к типам упражнений'}
              </Button>
            </div>

            {previewMode && (
              <Alert variant="info" className="py-2 px-3 mb-2" style={{ fontSize: '0.85rem', borderRadius: '0.5rem' }}>
                Режим просмотра — результат не сохраняется
              </Alert>
            )}

            <div className="d-flex align-items-start justify-content-between gap-3">
              <div>
                <h5
                  className="fw-bold mb-0"
                  style={{ letterSpacing: '-0.01em', color: '#1e2a4a', lineHeight: 1.35 }}
                >
                  📖 {variant?.exercise_name}
                </h5>
              </div>
              <div className="flex-shrink-0">
                {complexityLevel ? (
                  <Badge bg={LEVEL_VARIANT[complexityLevel.id] ?? 'secondary'} style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                    {complexityLevel.name}
                  </Badge>
                ) : complexityId ? (
                  <Badge bg={LEVEL_VARIANT[complexityId] ?? 'secondary'} style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                    {LEVEL_LABEL[complexityId] ?? complexityId}
                  </Badge>
                ) : null}
              </div>
            </div>
          </div>

          {/* ── Блок задания ── */}
          {variant?.exercise_task && (
            <div
              className="mb-3"
              style={{
                background: '#fff',
                borderRadius: '0.75rem',
                padding: '0.75rem 1rem',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  background: '#fff3cd',
                  color: '#856404',
                  borderRadius: '2rem',
                  padding: '0.2rem 0.65rem',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  marginBottom: '0.5rem',
                }}
              >
                ✦ Задание
              </span>
              <p className="mb-0" style={{ color: '#1e2a4a', fontSize: '0.9rem', lineHeight: 1.6 }}>
                {variant.exercise_task}
              </p>
            </div>
          )}

          {/* ── Прогресс ── */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span style={{ fontSize: '0.8125rem', color: '#6c757d', fontWeight: 500 }}>
                {isKeyword && totalItems > 1
                  ? `Предложение ${currentIdx + 1} из ${totalItems}`
                  : `Заполнено ${answeredCount} из ${totalBlanks}`}
              </span>
              <span style={{ fontSize: '0.8125rem', color: 'var(--bs-primary)', fontWeight: 600 }}>
                {progress}%
              </span>
            </div>
            <ProgressBar
              now={progress}
              style={{ height: '0.375rem', borderRadius: '1rem' }}
              variant={progress === 100 ? 'success' : 'primary'}
            />
          </div>

          {/* ── Основной контент ── */}
          <Card
            className="border-0"
            style={{ borderRadius: '0.875rem', boxShadow: '0 1px 6px rgba(0,0,0,0.07)', background: '#fff' }}
          >
            <Card.Body className="p-4">
              {isKeyword && currentEntry ? (
                <KeywordTransformItem
                  sentId={currentEntry[0]}
                  sentObj={currentEntry[1]}
                  answers={answers[currentEntry[0]] ?? {}}
                  onSetAnswer={(blankPos, value) => handleSetAnswer(currentEntry[0], blankPos, value)}
                  explanation={null}
                  firstInputRef={firstInputRef}
                />
              ) : isLexics ? (
                <LexicsTextItem
                  tokens={lexicsTokens}
                  wordsBank={lexicsWordsBank}
                  answers={answers[primarySentId] ?? {}}
                  onWordClick={handleWordClick}
                  onClearBlank={handleClearBlank}
                  explanation={null}
                />
              ) : (
                <div className="text-center py-4 text-muted" style={{ fontSize: '0.9rem' }}>
                  Нет данных для отображения
                </div>
              )}
            </Card.Body>
          </Card>

          {/* ── Панель кнопок (под карточкой) ── */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '0.875rem',
              padding: '0.75rem 1rem',
              background: '#fff',
              borderRadius: '0.75rem',
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}
          >
            {/* Левая часть: счётчик оставшихся */}
            <span style={{ fontSize: '0.8125rem', color: '#6c757d' }}>
              {totalBlanks - answeredCount > 0
                ? `Осталось заполнить: ${totalBlanks - answeredCount} ${
                    (totalBlanks - answeredCount) === 1 ? 'пропуск' :
                    (totalBlanks - answeredCount) < 5 ? 'пропуска' : 'пропусков'
                  }`
                : 'Все пропуски заполнены'}
            </span>

            {/* Правая часть: кнопки */}
            <div className="d-flex align-items-center gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => previewMode ? onInterrupt?.() : setShowInterruptModal(true)}
                style={{ borderRadius: '0.4rem', fontWeight: 500 }}
              >
                ↩ Выход
              </Button>

              {explanation && (
                <Button
                  variant="outline-info"
                  size="sm"
                  onClick={() => setShowRuleModal(true)}
                  style={{ borderRadius: '0.4rem', fontWeight: 500 }}
                >
                  🔍 Подсказка
                </Button>
              )}

              {currentIdx > 0 && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setCurrentIdx((i) => i - 1);
                    setProgressStep((s) => Math.max(0, s - 1));
                  }}
                  style={{ borderRadius: '0.4rem', fontWeight: 500 }}
                >
                  ← Назад
                </Button>
              )}

              {!isLastItem ? (
                <Button
                  size="sm"
                  onClick={() => {
                    setCurrentIdx((i) => i + 1);
                    setProgressStep((s) => Math.min(totalItems, s + 1));
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #4f6ef7 0%, #7c3aed 100%)',
                    border: 'none',
                    borderRadius: '0.4rem',
                    fontWeight: 600,
                  }}
                >
                  Вперёд →
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={() => {
                    setProgressStep(totalItems);
                    handleComplete();
                  }}
                  disabled={!isCurrentItemFilled || submitting}
                  style={{
                    background: isCurrentItemFilled
                      ? 'linear-gradient(135deg, #4f6ef7 0%, #7c3aed 100%)'
                      : undefined,
                    border: 'none',
                    borderRadius: '0.4rem',
                    fontWeight: 600,
                    opacity: isCurrentItemFilled ? 1 : 0.55,
                  }}
                >
                  {submitting
                    ? <><Spinner animation="border" size="sm" className="me-1" />Проверяем...</>
                    : '✓ Проверить'}
                </Button>
              )}
            </div>
          </div>

          {submitError && (
            <Alert variant="danger" className="mt-3" style={{ borderRadius: '0.75rem' }}>
              {submitError}
            </Alert>
          )}

        </Container>
      </div>

      {/* ── Модалка подсказки (грамматическое правило) ── */}
      <Modal show={showRuleModal} onHide={() => setShowRuleModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', padding: '1.25rem 1.5rem' }}>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 700 }}>
            {explanation?.title ?? 'Подсказка'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '1.5rem' }}>
          <p style={{ lineHeight: 1.7, color: '#1e2a4a', whiteSpace: 'pre-wrap', margin: 0 }}>
            {explanation?.description}
          </p>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid rgba(0,0,0,0.06)', padding: '1rem 1.5rem' }}>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowRuleModal(false)}
            style={{ borderRadius: '0.5rem', fontWeight: 500 }}>
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Модалка прерывания ── */}
      <Modal show={showInterruptModal} onHide={() => setShowInterruptModal(false)} centered size="sm">
        <Modal.Header closeButton style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', padding: '1.25rem 1.5rem' }}>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 700 }}>Прервать упражнение?</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '1.25rem 1.5rem' }}>
          <p className="mb-0 text-muted" style={{ fontSize: '0.9rem', lineHeight: 1.6 }}>
            Упражнение будет сохранено как незавершённое. Результат показан не будет.
          </p>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid rgba(0,0,0,0.06)', padding: '1rem 1.5rem' }}>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowInterruptModal(false)}
            style={{ borderRadius: '0.5rem', fontWeight: 500 }}
          >
            Продолжить
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={handleInterrupt}
            disabled={interrupting}
            style={{ borderRadius: '0.5rem', fontWeight: 600 }}
          >
            {interrupting ? 'Прерываем...' : 'Да, прервать'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
